import React, { useState, useRef } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

export default function AIAssetUploader() {
  const [uploadType, setUploadType] = useState('auto');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (files) => {
    setProcessing(true);
    const newFiles = Array.from(files);
    
    for (const file of newFiles) {
      if (file.type.startsWith('image/')) {
        // Simulate OCR processing
        await simulateOCRProcessing(file);
      }
    }
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    setProcessing(false);
  };

  const simulateOCRProcessing = async (file) => {
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    if (uploadType === 'auto') {
      // Simulate car data extraction
      const mockCarData = {
        licensePlate: 'ABC-123',
        vin: '1HGBH41JXMN109186',
        make: 'Honda',
        model: 'Civic',
        year: 2021,
        mileage: 45000,
        fuelType: 'Benzin',
        transmission: 'Automata',
        color: 'Fekete',
        estimatedValue: 8500000,
        condition: 'Kiváló',
        features: ['Klíma', 'Navigáció', 'Bluetooth', 'Tolatókamera']
      };
      setExtractedData(mockCarData);
    } else if (uploadType === 'property') {
      // Simulate property data extraction
      const mockPropertyData = {
        address: 'Budapest, V. kerület, Váci utca 12.',
        propertyType: 'Lakás',
        rooms: 3,
        size: 85,
        floor: 2,
        totalFloors: 4,
        yearBuilt: 1995,
        condition: 'Felújított',
        heating: 'Központi fűtés',
        estimatedValue: 65000000,
        features: ['Erkély', 'Lift', 'Parkolóhely', 'Pince']
      };
      setExtractedData(mockPropertyData);
    }
  };

  const generateAIDescription = (data) => {
    if (uploadType === 'auto') {
      return `Eladó egy ${data.year}-es ${data.make} ${data.model}, ${data.mileage.toLocaleString()} km futással. 
      A jármű ${data.condition.toLowerCase()} állapotban van, ${data.color.toLowerCase()} színű, ${data.transmission.toLowerCase()} váltóval és ${data.fuelType.toLowerCase()} üzemanyaggal. 
      Felszereltség: ${data.features.join(', ')}. 
      Becsült érték: ${data.estimatedValue.toLocaleString()} Ft.`;
    } else if (uploadType === 'property') {
      return `Eladó egy ${data.size} m²-es, ${data.rooms} szobás ${data.propertyType.toLowerCase()} ${data.address} címen. 
      Az ingatlan ${data.yearBuilt}-ben épült, ${data.condition.toLowerCase()} állapotban van. 
      A ${data.floor}. emeleten található egy ${data.totalFloors} emeletes épületben. 
      Fűtés: ${data.heating}. Extrák: ${data.features.join(', ')}. 
      Becsült érték: ${data.estimatedValue.toLocaleString()} Ft.`;
    }
    return '';
  };

  const generateAIVideo = async (data) => {
    setProcessing(true);
    // Simulate AI video generation
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const videoScript = uploadType === 'auto' 
      ? `Üdvözöljük! Bemutatjuk Önnek ezt a gyönyörű ${data.year}-es ${data.make} ${data.model}-t. 
         Ez a jármű kiváló állapotban van, mindössze ${data.mileage.toLocaleString()} kilométer futással. 
         A ${data.color.toLowerCase()} színű autó ${data.transmission.toLowerCase()} váltóval és modern felszereltséggel rendelkezik.`
      : `Bemutatkozik ez a csodálatos ${data.size} m²-es ingatlan ${data.address} címen. 
         A ${data.rooms} szobás ${data.propertyType.toLowerCase()} tökéletes választás családok számára. 
         Az ingatlan ${data.condition.toLowerCase()} állapotban van és számos extrával rendelkezik.`;

    alert(`AI videó generálva! Script: "${videoScript}"`);
    setProcessing(false);
  };

  const saveAsset = async () => {
    if (!extractedData) return;

    try {
      await addDoc(collection(db, 'assets'), {
        type: uploadType,
        data: extractedData,
        description: generateAIDescription(extractedData),
        images: uploadedFiles.map(f => f.name),
        createdAt: new Date(),
        status: 'active'
      });

      alert('Eszköz sikeresen mentve!');
      setExtractedData(null);
      setUploadedFiles([]);
    } catch (error) {
      console.error('Error saving asset:', error);
      alert('Hiba történt a mentés során!');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          AI Eszköz Feltöltő
        </h2>
        <select
          value={uploadType}
          onChange={(e) => setUploadType(e.target.value)}
          className="bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
        >
          <option value="auto">Autó</option>
          <option value="property">Ingatlan</option>
          <option value="mobilehome">Mobilház</option>
        </select>
      </div>

      {/* Upload Area */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
        <div 
          className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-[#e31937] transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            handleFileUpload(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
          />
          
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          
          <h3 className="text-white font-semibold mb-2">
            {uploadType === 'auto' ? 'Autó képek feltöltése' : 
             uploadType === 'property' ? 'Ingatlan képek feltöltése' : 
             'Mobilház képek feltöltése'}
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Húzza ide a képeket vagy kattintson a tallózáshoz
          </p>
          <p className="text-xs text-gray-500">
            Támogatott formátumok: JPG, PNG, WEBP
          </p>
        </div>

        {uploadedFiles.length > 0 && (
          <div className="mt-4">
            <h4 className="text-white font-medium mb-2">Feltöltött képek ({uploadedFiles.length})</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="bg-[#2a2a2a] rounded-lg p-2">
                  <div className="aspect-square bg-gray-700 rounded mb-2 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-white text-xs truncate">{file.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Processing */}
      {processing && (
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <div className="flex items-center justify-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#e31937]"></div>
            <span className="text-white">AI feldolgozás folyamatban...</span>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              OCR szövegfelismerés
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Adatok kinyerése
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              AI elemzés
            </div>
          </div>
        </div>
      )}

      {/* Extracted Data */}
      {extractedData && (
        <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
          <h3 className="text-white font-semibold mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            AI által kinyert adatok
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {uploadType === 'auto' && (
              <>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Rendszám</label>
                  <p className="text-white font-medium">{extractedData.licensePlate}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">VIN szám</label>
                  <p className="text-white font-medium">{extractedData.vin}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Márka/Modell</label>
                  <p className="text-white font-medium">{extractedData.make} {extractedData.model}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Évjárat</label>
                  <p className="text-white font-medium">{extractedData.year}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Futásteljesítmény</label>
                  <p className="text-white font-medium">{extractedData.mileage?.toLocaleString()} km</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Becsült érték</label>
                  <p className="text-green-400 font-bold text-lg">{extractedData.estimatedValue?.toLocaleString()} Ft</p>
                </div>
              </>
            )}

            {uploadType === 'property' && (
              <>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Cím</label>
                  <p className="text-white font-medium">{extractedData.address}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Típus</label>
                  <p className="text-white font-medium">{extractedData.propertyType}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Szobák száma</label>
                  <p className="text-white font-medium">{extractedData.rooms}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Alapterület</label>
                  <p className="text-white font-medium">{extractedData.size} m²</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Építési év</label>
                  <p className="text-white font-medium">{extractedData.yearBuilt}</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Becsült érték</label>
                  <p className="text-green-400 font-bold text-lg">{extractedData.estimatedValue?.toLocaleString()} Ft</p>
                </div>
              </>
            )}
          </div>

          {/* AI Generated Description */}
          <div className="mb-6">
            <label className="block text-gray-400 text-sm mb-2">AI által generált leírás</label>
            <div className="bg-[#2a2a2a] rounded-lg p-4">
              <p className="text-white text-sm leading-relaxed">{generateAIDescription(extractedData)}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={saveAsset}
              className="bg-[#e31937] hover:bg-[#b91c1c] text-white px-4 py-2 rounded-lg flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v4" />
              </svg>
              Mentés
            </button>
            
            <button
              onClick={() => generateAIVideo(extractedData)}
              disabled={processing}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              AI Videó Generálás
            </button>

            <button
              onClick={() => {
                setExtractedData(null);
                setUploadedFiles([]);
              }}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
            >
              Újrakezdés
            </button>
          </div>
        </div>
      )}

      {/* Features Info */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
        <h3 className="text-white font-semibold mb-4">AI Funkciók</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h4 className="text-white font-medium mb-2">OCR Felismerés</h4>
            <p className="text-gray-400 text-sm">Automatikus szöveg és adatok kinyerése képekből</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-white font-medium mb-2">Adatvalidáció</h4>
            <p className="text-gray-400 text-sm">VIN dekódolás, árbecslés és adatok ellenőrzése</p>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="text-white font-medium mb-2">Videó Generálás</h4>
            <p className="text-gray-400 text-sm">AI-alapú hirdetési videók automatikus készítése</p>
          </div>
        </div>
      </div>
    </div>
  );
}
